/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Documents component for managing company document uploads.
 * This component handles file uploads for various business documents including
 * general documents, brochures, tax ID certificates, compliance opinions,
 * liability policies, IMSS registration, REPSE, and certification proofs.
 */

import React, { useState, useEffect, useRef } from "react";
import styles from "./Documents.module.css";
import { useTranslation } from "react-i18next";
import FileUploadArea from "../../../modals/ProposalCreateModal/FileUploadArea";
import {
  createFileObjectFromUrl,
  getCompanyFieldValue,
  shouldHideFieldForProfile,
} from "../../../utils/common";

/** MIME type constant for PDF files */
const FILE_TYPE_PDF = "application/pdf";

/** MIME type constant for JPEG images */
const FILE_TYPE_JPEG = "image/jpeg";

/** MIME type constant for JPG images (alternative) */
const FILE_TYPE_JPG = "image/jpg";

/** MIME type constant for PNG images */
const FILE_TYPE_PNG = "image/png";

/** Translation key for file type validation error message */
const DEFAULT_FILE_TYPE_ERROR_KEY = "components.documents.defaultFileTypeError";

/** Translation key for file size validation error message */
const DEFAULT_FILE_SIZE_ERROR_KEY = "components.documents.defaultFileSizeError";

/**
 * Documents component for displaying and managing company document uploads.
 * Handles file uploads with validation for various business documents including
 * tax certificates, compliance documents, insurance policies, and certifications.
 *
 * @param {Object} props - Component props
 * @param {Object} props.company - Company data object containing document URLs
 * @param {Function} props.onFieldChange - Callback function triggered when a file is uploaded or removed
 * @param {Function} props.onValidationChange - Callback function triggered when validation state changes
 * @param {number} [props.selectedProfile=0] - Currently selected company profile index for multi-profile support
 * @returns {JSX.Element} The rendered Documents form section
 */
const Documents = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const { t } = useTranslation();
  const prevHasErrorsRef = useRef(false);
  const onValidationChangeRef = useRef(onValidationChange);

  const isTablet = window.innerWidth <= 1023;

  const [documentsFile, setDocumentsFile] = useState(
    createFileObjectFromUrl(
      getCompanyFieldValue(company, "documentos", selectedProfile, null),
    ),
  );
  const [brochureFile, setBrochureFile] = useState(
    createFileObjectFromUrl(
      getCompanyFieldValue(company, "brochure", selectedProfile, null),
    ),
  );
  const [taxIDFile, setTaxIDFile] = useState(
    createFileObjectFromUrl(company?.cedulaFiscal),
  );
  const [positiveComplianceOpinionFile, setPositiveComplianceOpinionFile] =
    useState(createFileObjectFromUrl(company?.opinionDeCumplimientoPositiva));
  const [civilLiabilityPolicyFile, setCivilLiabilityPolicyFile] = useState(
    createFileObjectFromUrl(
      getCompanyFieldValue(
        company,
        "polizaDeResponsabilidadCivil",
        selectedProfile,
        null,
      ),
    ),
  );
  const [IMSSRegistrationFile, setIMSSRegistrationFile] = useState(
    createFileObjectFromUrl(company?.altaIMSS),
  );
  const [REPSEFile, setREPSEFile] = useState(
    createFileObjectFromUrl(company?.REPSE),
  );
  const [proofOfCertificationFile, setProofOfCertificationFile] = useState(
    createFileObjectFromUrl(
      getCompanyFieldValue(
        company,
        "comprobanteDeCertificacion",
        selectedProfile,
        null,
      ),
    ),
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [documentsError, setDocumentsError] = useState("");
  const [brochureError, setBrochureError] = useState("");
  const [taxIDError, setTaxIDError] = useState("");
  const [positiveComplianceOpinionError, setPositiveComplianceOpinionError] =
    useState("");
  const [civilLiabilityPolicyError, setCivilLiabilityPolicyError] =
    useState("");
  const [IMSSRegistrationError, setIMSSRegistrationError] = useState("");
  const [REPSEError, setREPSEError] = useState("");
  const [proofOfCertificationError, setProofOfCertificationError] =
    useState("");

  const defaultImageTypes = [
    FILE_TYPE_PDF,
    FILE_TYPE_JPEG,
    FILE_TYPE_JPG,
    FILE_TYPE_PNG,
  ];
  const defaultErrorMessages = {
    type: t(DEFAULT_FILE_TYPE_ERROR_KEY),
    size: t(DEFAULT_FILE_SIZE_ERROR_KEY),
  };

  const fileRequirements = {
    documentos: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: [
        FILE_TYPE_PDF,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      errorMessages: {
        type: t("components.documents.documentsFileTypeError"),
        size: t("components.documents.documentsFileSizeError"),
      },
      setError: setDocumentsError,
    },
    brochure: {
      maxSize: 15 * 1024 * 1024, // 15MB
      allowedTypes: defaultImageTypes,
      errorMessages: {
        type: t("components.documents.brochureFileTypeError"),
        size: t("components.documents.brochureFileSizeError"),
      },
      setError: setBrochureError,
    },
    cedulaFiscal: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: defaultImageTypes,
      errorMessages: defaultErrorMessages,
      setError: setTaxIDError,
    },
    opinionDeCumplimientoPositiva: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: defaultImageTypes,
      errorMessages: defaultErrorMessages,
      setError: setPositiveComplianceOpinionError,
    },
    polizaDeResponsabilidadCivil: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: defaultImageTypes,
      errorMessages: defaultErrorMessages,
      setError: setCivilLiabilityPolicyError,
    },
    altaIMSS: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: defaultImageTypes,
      errorMessages: defaultErrorMessages,
      setError: setIMSSRegistrationError,
    },
    REPSE: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: defaultImageTypes,
      errorMessages: defaultErrorMessages,
      setError: setREPSEError,
    },
    comprobanteDeCertificacion: {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: defaultImageTypes,
      errorMessages: defaultErrorMessages,
      setError: setProofOfCertificationError,
    },
  };

  const validateFile = (file, fileType) => {
    const requirements = fileRequirements[fileType];

    if (!requirements.allowedTypes.includes(file.type)) {
      requirements.setError(requirements.errorMessages.type);
      return false;
    }

    if (file.size > requirements.maxSize) {
      requirements.setError(requirements.errorMessages.size);
      return false;
    }

    return true;
  };

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const handleFileClick = (fileInputId) => {
    document.getElementById(fileInputId).click();
  };

  const handleFileChange = (e, setFileFunction, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (validateFile(file, fileType)) {
        setFileFunction(file);
        fileRequirements[fileType].setError("");
        handleInputChange(fileType, file);
      } else {
        setFileFunction(null);
        handleInputChange(fileType, null);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e, setFileFunction, fileType) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      if (validateFile(file, fileType)) {
        setFileFunction(file);
        fileRequirements[fileType].setError("");
        handleInputChange(fileType, file);
      } else {
        setFileFunction(null);
        handleInputChange(fileType, null);
      }
    }
  };

  const hasFileErrors = () => {
    return !!(
      documentsError ||
      brochureError ||
      taxIDError ||
      positiveComplianceOpinionError ||
      civilLiabilityPolicyError ||
      IMSSRegistrationError ||
      REPSEError ||
      proofOfCertificationError
    );
  };

  /**
   * Pre-bound file change handlers to avoid duplicate inline functions.
   * Each handler is reused for both onFileChange prop and hidden input onChange.
   */
  const handlePositiveComplianceOpinionChange = (e) =>
    handleFileChange(
      e,
      setPositiveComplianceOpinionFile,
      "opinionDeCumplimientoPositiva",
    );

  const handleCivilLiabilityPolicyChange = (e) =>
    handleFileChange(
      e,
      setCivilLiabilityPolicyFile,
      "polizaDeResponsabilidadCivil",
    );

  const handleProofOfCertificationChange = (e) =>
    handleFileChange(
      e,
      setProofOfCertificationFile,
      "comprobanteDeCertificacion",
    );

  // Update the ref when onValidationChange changes
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  }, [onValidationChange]);

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    const currentHasErrors = hasFileErrors();
    if (
      onValidationChangeRef.current &&
      prevHasErrorsRef.current !== currentHasErrors
    ) {
      prevHasErrorsRef.current = currentHasErrors;
      onValidationChangeRef.current(currentHasErrors);
    }
  }, [
    documentsError,
    brochureError,
    taxIDError,
    positiveComplianceOpinionError,
    civilLiabilityPolicyError,
    IMSSRegistrationError,
    REPSEError,
    proofOfCertificationError,
  ]);

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.documents.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.documents.documentsLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={documentsFile}
              isUploading={isUploading}
              isDragging={isDragging}
              error={documentsError}
              onFileChange={(e) =>
                handleFileChange(e, setDocumentsFile, "documentos")
              }
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, setDocumentsFile, "documentos")}
              onClick={() => handleFileClick("documentsFileInput")}
              reduceHeightForTablet={isTablet}
            />
            <input
              id="documentsFileInput"
              type="file"
              onChange={(e) =>
                handleFileChange(e, setDocumentsFile, "documentos")
              }
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.documents.brochureLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={brochureFile}
              isUploading={isUploading}
              isDragging={isDragging}
              error={brochureError}
              onFileChange={(e) =>
                handleFileChange(e, setBrochureFile, "brochure")
              }
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, setBrochureFile, "brochure")}
              onClick={() => handleFileClick("brochureFileInput")}
              reduceHeightForTablet={isTablet}
            />
            <input
              id="brochureFileInput"
              type="file"
              onChange={(e) => handleFileChange(e, setBrochureFile, "brochure")}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
        {!shouldHideFieldForProfile("cedulaFiscal", selectedProfile) && (
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              {t("components.documents.taxIDLabel")}
            </label>
            <div className={styles.fileUploadContainer}>
              <FileUploadArea
                file={taxIDFile}
                isUploading={isUploading}
                isDragging={isDragging}
                error={taxIDError}
                onFileChange={(e) =>
                  handleFileChange(e, setTaxIDFile, "cedulaFiscal")
                }
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, setTaxIDFile, "cedulaFiscal")}
                onClick={() => handleFileClick("taxIDFileInput")}
                reduceHeightForTablet={isTablet}
              />
              <input
                id="taxIDFileInput"
                type="file"
                onChange={(e) =>
                  handleFileChange(e, setTaxIDFile, "cedulaFiscal")
                }
                className={styles.fileInput}
                style={{ display: "none" }}
              />
            </div>
          </div>
        )}
        {!shouldHideFieldForProfile(
          "opinionDeCumplimientoPositiva",
          selectedProfile,
        ) && (
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              {t("components.documents.positiveComplianceOpinionLabel")}
            </label>
            <div className={styles.fileUploadContainer}>
              <FileUploadArea
                file={positiveComplianceOpinionFile}
                isUploading={isUploading}
                isDragging={isDragging}
                error={positiveComplianceOpinionError}
                onFileChange={handlePositiveComplianceOpinionChange}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) =>
                  handleDrop(
                    e,
                    setPositiveComplianceOpinionFile,
                    "opinionDeCumplimientoPositiva",
                  )
                }
                onClick={() =>
                  handleFileClick("positiveComplianceOpinionFileInput")
                }
                reduceHeightForTablet={isTablet}
              />
              <input
                id="positiveComplianceOpinionFileInput"
                type="file"
                onChange={handlePositiveComplianceOpinionChange}
                className={styles.fileInput}
                style={{ display: "none" }}
              />
            </div>
          </div>
        )}
      </div>
      <div className={styles.inputRow}>
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.documents.civilLiabilityPolicyLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={civilLiabilityPolicyFile}
              isUploading={isUploading}
              isDragging={isDragging}
              error={civilLiabilityPolicyError}
              onFileChange={handleCivilLiabilityPolicyChange}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) =>
                handleDrop(
                  e,
                  setCivilLiabilityPolicyFile,
                  "polizaDeResponsabilidadCivil",
                )
              }
              onClick={() => handleFileClick("civilLiabilityPolicyFileInput")}
              reduceHeightForTablet={isTablet}
            />
            <input
              id="civilLiabilityPolicyFileInput"
              type="file"
              onChange={handleCivilLiabilityPolicyChange}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
        {!shouldHideFieldForProfile("altaIMSS", selectedProfile) && (
          <div className={`${styles.inputGroup}`}>
            <label className={styles.inputLabel}>
              {t("components.documents.IMSSRegistrationLabel")}
            </label>
            <div className={styles.fileUploadContainer}>
              <FileUploadArea
                file={IMSSRegistrationFile}
                isUploading={isUploading}
                isDragging={isDragging}
                error={IMSSRegistrationError}
                onFileChange={(e) =>
                  handleFileChange(e, setIMSSRegistrationFile, "altaIMSS")
                }
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) =>
                  handleDrop(e, setIMSSRegistrationFile, "altaIMSS")
                }
                onClick={() => handleFileClick("IMSSRegistrationFileInput")}
                reduceHeightForTablet={isTablet}
              />
              <input
                id="IMSSRegistrationFileInput"
                type="file"
                onChange={(e) =>
                  handleFileChange(e, setIMSSRegistrationFile, "altaIMSS")
                }
                className={styles.fileInput}
                style={{ display: "none" }}
              />
            </div>
          </div>
        )}
        {!shouldHideFieldForProfile("REPSE", selectedProfile) && (
          <div className={`${styles.inputGroup}`}>
            <label className={styles.inputLabel}>
              {t("components.documents.REPSELabel")}
            </label>
            <div className={styles.fileUploadContainer}>
              <FileUploadArea
                file={REPSEFile}
                isUploading={isUploading}
                isDragging={isDragging}
                error={REPSEError}
                onFileChange={(e) => handleFileChange(e, setREPSEFile, "REPSE")}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, setREPSEFile, "REPSE")}
                onClick={() => handleFileClick("REPSEFileInput")}
                reduceHeightForTablet={isTablet}
              />
              <input
                id="REPSEFileInput"
                type="file"
                onChange={(e) => handleFileChange(e, setREPSEFile, "REPSE")}
                className={styles.fileInput}
                style={{ display: "none" }}
              />
            </div>
          </div>
        )}
        <div className={`${styles.inputGroup}`}>
          <label className={styles.inputLabel}>
            {t("components.documents.proofOfCertificationLabel")}
          </label>
          <div className={styles.fileUploadContainer}>
            <FileUploadArea
              file={proofOfCertificationFile}
              isUploading={isUploading}
              isDragging={isDragging}
              error={proofOfCertificationError}
              onFileChange={handleProofOfCertificationChange}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) =>
                handleDrop(
                  e,
                  setProofOfCertificationFile,
                  "comprobanteDeCertificacion",
                )
              }
              onClick={() => handleFileClick("proofOfCertificationFileInput")}
              reduceHeightForTablet={isTablet}
            />
            <input
              id="proofOfCertificationFileInput"
              type="file"
              onChange={handleProofOfCertificationChange}
              className={styles.fileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
